import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Flag, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useToast } from '@/hooks/use-toast';

const FeatureFlagsPanel: React.FC = () => {
  const { flagDetails, loading, refreshFlags, updateFlag } = useFeatureFlags();
  const { toast } = useToast();
  const [updating, setUpdating] = React.useState<string | null>(null);

  const handleToggle = async (key: string, currentValue: boolean) => {
    setUpdating(key);
    try {
      await updateFlag(key, !currentValue);
      toast({
        title: 'Feature flag actualizado',
        description: `"${key}" ${!currentValue ? 'habilitado' : 'deshabilitado'}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el feature flag',
        variant: 'destructive',
      });
    } finally {
      setUpdating(null);
    }
  };

  const getKeyDisplayName = (key: string): string => {
    const names: Record<string, string> = {
      'background_removal': 'Quitar Fondo',
      
      'batch_download': 'Descarga Masiva ZIP',
      'premium_features': 'Funcionalidades Premium',
    };
    return names[key] || key;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-purple-600" />
              Feature Flags
            </CardTitle>
            <CardDescription>
              Habilita o deshabilita funcionalidades sin modificar código
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshFlags}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && flagDetails.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            Cargando feature flags...
          </div>
        ) : flagDetails.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No hay feature flags configurados
          </div>
        ) : (
          <div className="space-y-4">
            {flagDetails.map((flag) => (
              <div
                key={flag.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="space-y-1">
                  <Label 
                    htmlFor={flag.key} 
                    className="text-base font-medium cursor-pointer"
                  >
                    {getKeyDisplayName(flag.key)}
                  </Label>
                  {flag.description && (
                    <p className="text-sm text-muted-foreground">
                      {flag.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground font-mono">
                    key: {flag.key}
                  </p>
                </div>
                <Switch
                  id={flag.key}
                  checked={flag.enabled}
                  onCheckedChange={() => handleToggle(flag.key, flag.enabled)}
                  disabled={updating === flag.key}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeatureFlagsPanel;
